import { QueryClient, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect } from 'react'
import { useSetRecoilState } from 'recoil'

import { axiosInstance } from '../../../axios/axiosInstance'
import {
  userCoreData,
  userData,
  userLegacyData,
} from '../../../recoil/atoms/userDataAtom'
import { urls } from '../../../services/auth-service/auth.service'
import {
  getToken,
  hasTokenExpired,
} from '../../../services/token-service/token.service'
import { setUserLocalData } from '../../../services/user-service/user.service'
import { handlePlatformVersionChange } from '../../../util/platformVersion'
import { YotiFlowType } from '../../../V2/model/enum/YotiFlowType.enum'
import { ApiResponseBasicProfile } from '../../../V2/model/interfaces/MeUserData'

export interface UserProfile {
  id: number
  username: string
  email: string
  terms_and_conditions: number
  created_at: string
  updated_at: string
  deleted_at: string | null
  email_verified_at: string | null
  auto_verified_at: string | null
  click_id: number | null
  freshchat_restore_id: string | null
  has_spent_before: boolean
  user_has_card_removed: boolean
  is_age_verified?: boolean | number
  age_verification_required?: boolean
  yoti_flow_type?: YotiFlowType | null
  use_only_yoti_verification_selfie_and_id?: boolean
  age_verification?: {
    is_age_verified: boolean
    age_verification_required: boolean
    yoti_flow_type: YotiFlowType | null
  }
  profile: {
    id: number
    username: string
    real_name: string | null
    gender: string
    seeking: string
    city: string
    zip_code: string
    birthday: string
    user_id: number
    description: string | null
    profile_image: string | null
    body_build: string | null
    hair_color: string | null
    eye_color: string | null
    interests: string[]
    personal_interests: string[]
    relationship_interest: string | null
    ethnicity: string | null
    civil_status: string | null
    height: string | null
    online_status: number
    show_online_status: number
    private_location: number
    avatar_image: string | null
    is_new?: boolean
    is_adult_avatar?: boolean
    is_adult_avatar_locked?: boolean
  }
  favorite_profiles: any[]
  credits: number
  totalCreditsBought: number
  location_confirmed: number
  activity_score: number
  last_active_at: string
  premium: boolean
  hasPremiumSubscription: boolean
  roles: {
    id: number
    name: string
  }[]
  billing_date: string | null
  credit_history: {
    amount: number
    action: string
    issuer: string | null
    created_at: string
  }[]
}

export const key = ['USER_DATA']
export const meCoreKey = ['USER_CORE_DATA']
export const meLegacyKey = ['USER_LEGACY_DATA']

export const ME_LEGACY_URL = urls.me
export const ME_CORE_URL = `${urls.me}/core`

const LEGACY_REFRESH_COOLDOWN_MS = 1500
const CORE_REFRESH_COOLDOWN_MS = 500

let legacyRefreshPromise: Promise<ApiResponseBasicProfile> | null = null
let coreRefreshPromise: Promise<ApiResponseBasicProfile> | null = null
let lastFetchedCoreResponse: ApiResponseBasicProfile | undefined
let lastFetchedLegacyResponse: ApiResponseBasicProfile | undefined
let lastLegacyRefreshAt = 0
let lastCoreRefreshAt = 0

const getAgeVerificationValue = (coreUser?: any, legacyUser?: any) => {
  const ageVerification = coreUser?.age_verification

  return {
    is_age_verified:
      ageVerification?.is_age_verified ?? legacyUser?.is_age_verified ?? false,
    age_verification_required:
      ageVerification?.age_verification_required ??
      legacyUser?.age_verification_required ??
      false,
    yoti_flow_type:
      ageVerification?.yoti_flow_type ?? legacyUser?.yoti_flow_type ?? null,
  }
}

export const normalizeCoreUserResponse = (
  response?: ApiResponseBasicProfile | null,
): ApiResponseBasicProfile | undefined => {
  if (!response?.data) return response ?? undefined

  const ageVerification = getAgeVerificationValue(response.data)

  return {
    ...response,
    data: {
      ...response.data,
      is_age_verified: ageVerification.is_age_verified,
      age_verification_required: ageVerification.age_verification_required,
      yoti_flow_type: ageVerification.yoti_flow_type,
      age_verification: {
        is_age_verified: ageVerification.is_age_verified,
        age_verification_required: ageVerification.age_verification_required,
        yoti_flow_type: ageVerification.yoti_flow_type,
      },
    },
  }
}

export const mergeUserResponses = (
  legacyResponse?: ApiResponseBasicProfile | null,
  coreResponse?: ApiResponseBasicProfile | null,
): ApiResponseBasicProfile | undefined => {
  const legacyUser = legacyResponse?.data
  const coreUser = coreResponse?.data

  if (!legacyUser && !coreUser) return undefined

  const ageVerification = getAgeVerificationValue(coreUser, legacyUser)
  const mergedProfile = {
    ...(legacyUser?.profile ?? {}),
    ...(coreUser?.profile ?? {}),
  }

  return {
    data: {
      ...(legacyUser ?? {}),
      ...(coreUser ?? {}),
      profile: mergedProfile,
      is_age_verified: ageVerification.is_age_verified,
      age_verification_required: ageVerification.age_verification_required,
      yoti_flow_type: ageVerification.yoti_flow_type,
      age_verification: {
        is_age_verified: ageVerification.is_age_verified,
        age_verification_required: ageVerification.age_verification_required,
        yoti_flow_type: ageVerification.yoti_flow_type,
      },
    } as any,
  }
}

export const fetchMeCore = async (): Promise<ApiResponseBasicProfile> => {
  const { data } = await axiosInstance.get(ME_CORE_URL)
  const response = normalizeCoreUserResponse(data) as ApiResponseBasicProfile

  // Only /me/core drives platform version routing. `mergeUserResponses` also
  // treats core as authoritative for this field, and `fetchUserData` requests
  // core and legacy concurrently — so reading it from both would make the
  // cookie last-response-wins whenever the two endpoints disagree.
  handlePlatformVersionChange(response?.data?.platform_version)

  return response
}

export const fetchMeLegacy = async (): Promise<ApiResponseBasicProfile> => {
  const { data } = await axiosInstance.get(ME_LEGACY_URL)
  return data
}

export const fetchUserData = async (): Promise<ApiResponseBasicProfile> => {
  const [coreResult, legacyResult] = await Promise.allSettled([
    fetchMeCore(),
    fetchMeLegacy(),
  ])

  const coreResponse =
    coreResult.status === 'fulfilled' ? coreResult.value : undefined
  const legacyResponse =
    legacyResult.status === 'fulfilled' ? legacyResult.value : undefined

  lastFetchedCoreResponse = coreResponse
  lastFetchedLegacyResponse = legacyResponse
  const mergedResponse = mergeUserResponses(legacyResponse, coreResponse)

  if (!mergedResponse) {
    if (coreResult.status === 'rejected') throw coreResult.reason
    if (legacyResult.status === 'rejected') throw legacyResult.reason
    throw new Error('Unable to fetch user data')
  }

  return mergedResponse
}

const syncUserDataCache = (
  queryClient: QueryClient,
  coreResponse?: ApiResponseBasicProfile | null,
  legacyResponse?: ApiResponseBasicProfile | null,
) => {
  const currentCore =
    queryClient.getQueryData<ApiResponseBasicProfile>(meCoreKey)
  const currentLegacy =
    queryClient.getQueryData<ApiResponseBasicProfile>(meLegacyKey)

  const nextCore = normalizeCoreUserResponse(coreResponse) ?? currentCore
  const nextLegacy = legacyResponse ?? currentLegacy
  const mergedResponse = mergeUserResponses(nextLegacy, nextCore)

  if (nextCore) queryClient.setQueryData(meCoreKey, nextCore)
  if (nextLegacy) queryClient.setQueryData(meLegacyKey, nextLegacy)
  if (mergedResponse) {
    queryClient.setQueryData(key, mergedResponse)
    setUserLocalData(mergedResponse)
  }

  return mergedResponse
}

export const refreshCoreUserData = async (
  queryClient: QueryClient,
  options: { force?: boolean } = {},
) => {
  const now = Date.now()
  const cached = queryClient.getQueryData<ApiResponseBasicProfile>(meCoreKey)

  if (
    !options.force &&
    cached &&
    now - lastCoreRefreshAt < CORE_REFRESH_COOLDOWN_MS
  ) {
    return syncUserDataCache(queryClient, cached, undefined)
  }

  if (coreRefreshPromise) {
    const coreResponse = await coreRefreshPromise
    return syncUserDataCache(queryClient, coreResponse, undefined)
  }

  coreRefreshPromise = fetchMeCore()

  try {
    const coreResponse = await coreRefreshPromise
    lastCoreRefreshAt = Date.now()
    return syncUserDataCache(queryClient, coreResponse, undefined)
  } finally {
    coreRefreshPromise = null
  }
}

export const refreshLegacyUserData = async (
  queryClient: QueryClient,
  options: { force?: boolean } = {},
) => {
  const now = Date.now()
  const cached = queryClient.getQueryData<ApiResponseBasicProfile>(meLegacyKey)

  if (
    !options.force &&
    cached &&
    now - lastLegacyRefreshAt < LEGACY_REFRESH_COOLDOWN_MS
  ) {
    return syncUserDataCache(queryClient, undefined, cached)
  }

  if (legacyRefreshPromise) {
    const legacyResponse = await legacyRefreshPromise
    return syncUserDataCache(queryClient, undefined, legacyResponse)
  }

  legacyRefreshPromise = fetchMeLegacy()

  try {
    const legacyResponse = await legacyRefreshPromise
    lastLegacyRefreshAt = Date.now()
    return syncUserDataCache(queryClient, undefined, legacyResponse)
  } finally {
    legacyRefreshPromise = null
  }
}

export const refreshUserData = async (
  queryClient: QueryClient,
  options: { force?: boolean } = {},
) => {
  const [coreResponse, legacyResponse] = await Promise.all([
    refreshCoreUserData(queryClient, options),
    refreshLegacyUserData(queryClient, options),
  ])

  return syncUserDataCache(
    queryClient,
    queryClient.getQueryData<ApiResponseBasicProfile>(meCoreKey) ??
      coreResponse,
    queryClient.getQueryData<ApiResponseBasicProfile>(meLegacyKey) ??
      legacyResponse,
  )
}

export const useGetUserData = () => {
  const queryClient = useQueryClient()
  const setUserData = useSetRecoilState(userData)
  const setCoreUserData = useSetRecoilState(userCoreData)
  const setLegacyUserData = useSetRecoilState(userLegacyData)

  const isAuthenticated = Boolean(getToken()) && !hasTokenExpired()
  const isAutoLoginInProgress =
    typeof window !== 'undefined' &&
    (() => {
      const params = new URLSearchParams(window.location.search)
      return Boolean(
        params.get('hash') &&
          params.get('id') &&
          (params.get('action') === 'login' || params.has('verified')),
      )
    })()

  const query = useQuery(key, fetchUserData, {
    enabled: isAuthenticated && !isAutoLoginInProgress,
    staleTime: Infinity,
    cacheTime: Infinity,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: 1,
    onSuccess: (data: ApiResponseBasicProfile) => {
      if (lastFetchedCoreResponse)
        queryClient.setQueryData(meCoreKey, lastFetchedCoreResponse)
      if (lastFetchedLegacyResponse)
        queryClient.setQueryData(meLegacyKey, lastFetchedLegacyResponse)
      setUserLocalData(data)
      setUserData(data)
      if (lastFetchedCoreResponse) setCoreUserData(lastFetchedCoreResponse)
      if (lastFetchedLegacyResponse)
        setLegacyUserData(lastFetchedLegacyResponse)
    },
  })

  useEffect(() => {
    if (!query.data) return

    const coreResponse =
      queryClient.getQueryData<ApiResponseBasicProfile>(meCoreKey)
    const legacyResponse =
      queryClient.getQueryData<ApiResponseBasicProfile>(meLegacyKey)

    setUserData(query.data)
    if (coreResponse) setCoreUserData(coreResponse)
    if (legacyResponse) setLegacyUserData(legacyResponse)
    setUserLocalData(query.data)
  }, [query.data, queryClient, setCoreUserData, setLegacyUserData, setUserData])

  return query
}
